import React, { createContext, useContext, useState, useCallback } from 'react';
import NotificationContainer from '../components/NotificationContainer';

const NotificationContext = createContext();

export const useNotify = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const showNotification = useCallback(({
        type = 'info',
        title = '',
        message = '',
        duration = 5000,
        showClose = true,
        showIcon = true,
        autoClose = true,
        actions = [],
        onClose,
        onActionClick
    }) => {
        const id = Date.now() + Math.random();
        const newNotification = {
            id,
            type,
            title,
            message,
            duration,
            showClose: showClose !== false, // Default true
            showIcon: showIcon !== false, // Default true
            autoClose: autoClose !== false, // Default true
            actions,
            onClose,
            onActionClick,
            timestamp: new Date().toISOString()
        };

        setNotifications(prev => [...prev, newNotification]);

        if (autoClose !== false && duration > 0) {
            setTimeout(() => {
                removeNotification(id);
                if (onClose) onClose();
            }, duration);
        }

        return id;
    }, []);

    const showSuccess = useCallback((title, message = '', options = {}) => {
        return showNotification({
            type: 'success',
            title,
            message,
            ...options
        });
    }, [showNotification]);

    const showError = useCallback((title, message = '', options = {}) => {
        return showNotification({
            type: 'error',
            title,
            message,
            ...options
        });
    }, [showNotification]);

    const showWarning = useCallback((title, message = '', options = {}) => {
        return showNotification({
            type: 'warning',
            title,
            message,
            ...options
        });
    }, [showNotification]);

    const showInfo = useCallback((title, message = '', options = {}) => {
        return showNotification({
            type: 'info',
            title,
            message,
            ...options
        });
    }, [showNotification]);

    const showConfirm = useCallback(({
        title,
        message,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        onConfirm,
        onCancel,
        type = 'warning'
    }) => {
        const id = Date.now() + Math.random();
        
        const notification = {
            id,
            type,
            title,
            message,
            autoClose: false,
            showClose: false,
            actions: [
                {
                    text: cancelText,
                    variant: 'outline',
                    onClick: () => {
                        if (onCancel) onCancel();
                        removeNotification(id);
                    }
                },
                {
                    text: confirmText,
                    variant: 'primary',
                    onClick: () => {
                        if (onConfirm) onConfirm();
                        removeNotification(id);
                    }
                }
            ],
            timestamp: new Date().toISOString()
        };

        setNotifications(prev => [...prev, notification]);
        return id;
    }, []);

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const value = {
        notifications,
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showConfirm,
        removeNotification,
        clearAllNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <NotificationContainer />
        </NotificationContext.Provider>
    );
};